import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/api";
import { toast } from "sonner";
import { LogOut, Download, Plus, Trash2, Check, X, Upload } from "lucide-react";

const STATUS = { pending: "În așteptare", confirmed: "Confirmată", cancelled: "Anulată" };
const STATUS_CLS = {
  pending: "bg-sage/25 text-green-dark",
  confirmed: "bg-green-dark text-cream-light",
  cancelled: "bg-warm-grey/40 text-brown/60",
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [units, setUnits] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [media, setMedia] = useState([]);
  const [config, setConfig] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newB, setNewB] = useState({ unit_id: "", guest_name: "", phone: "", check_in: "", check_out: "", notes: "", deposit: 0 });
  const [newBlock, setNewBlock] = useState({ unit_id: "", start_date: "", end_date: "", reason: "" });
  const [newPrice, setNewPrice] = useState({ unit_id: "", start_date: "", end_date: "", price_weekday: "", price_weekend: "", minimum_nights: 1, label: "" });
  const [settingsForm, setSettingsForm] = useState({});
  const [uploadMeta, setUploadMeta] = useState({ category: "general", alt_ro: "" });

  const load = async () => {
    try {
      const [s, u, b, bl, p, m, c] = await Promise.all([
        api.get("/admin/stats"), api.get("/units"), api.get("/admin/bookings"),
        api.get("/admin/blocks"), api.get("/admin/pricing"), api.get("/media"), api.get("/config"),
      ]);
      setStats(s.data); setUnits(u.data); setBookings(b.data); setBlocks(bl.data);
      setPricing(p.data); setMedia(m.data); setConfig(c.data);
      setSettingsForm(c.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("ih_token")) { navigate("/admin/login"); return; }
    load();
  }, []);

  const patchBooking = async (id, patch) => {
    try {
      await api.patch(`/admin/bookings/${id}`, patch);
      toast.success("Actualizat");
      load();
    } catch (e) {
      toast.error(e.response?.status === 409 ? "Suprapunere cu o rezervare confirmată" : "Eroare");
    }
  };

  const addBooking = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/bookings", { ...newB, deposit: +newB.deposit || 0 });
      toast.success("Rezervare adăugată");
      setShowAdd(false);
      setNewB({ unit_id: "", guest_name: "", phone: "", check_in: "", check_out: "", notes: "", deposit: 0 });
      load();
    } catch (e) {
      toast.error(e.response?.status === 409 ? "Suprapunere cu o rezervare confirmată" : "Verifică datele");
    }
  };

  const addBlock = async (e) => {
    e.preventDefault();
    await api.post("/admin/blocks", newBlock);
    toast.success("Perioadă blocată");
    setNewBlock({ unit_id: "", start_date: "", end_date: "", reason: "" });
    load();
  };

  const addPrice = async (e) => {
    e.preventDefault();
    await api.post("/admin/pricing", {
      ...newPrice,
      unit_id: newPrice.unit_id || null,
      price_weekday: newPrice.price_weekday ? +newPrice.price_weekday : null,
      price_weekend: newPrice.price_weekend ? +newPrice.price_weekend : null,
      minimum_nights: +newPrice.minimum_nights || 1,
    });
    toast.success("Regulă de preț adăugată");
    setNewPrice({ unit_id: "", start_date: "", end_date: "", price_weekday: "", price_weekend: "", minimum_nights: 1, label: "" });
    load();
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    await api.put("/admin/settings", { values: settingsForm });
    toast.success("Setări salvate");
    load();
  };

  const saveUnitPrice = async (id, price) => {
    await api.patch(`/admin/units/${id}`, { base_price: +price });
    toast.success("Preț actualizat");
    load();
  };

  const exportCsv = async () => {
    const r = await api.get("/admin/export/bookings", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rezervari-imperfect-house.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportQuiz = async () => {
    const r = await api.get("/admin/export/quiz", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "quiz-imperfect-house.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", uploadMeta.category);
    fd.append("alt_ro", uploadMeta.alt_ro);
    try {
      await api.post("/admin/media", fd);
      toast.success("Imagine încărcată");
      load();
    } catch {
      toast.error("Încărcarea a eșuat");
    }
    e.target.value = "";
  };

  const logout = () => { localStorage.removeItem("ih_token"); navigate("/admin/login"); };

  const tabs = [
    ["bookings", "Rezervări"], ["calendar", "Calendar & blocări"], ["pricing", "Prețuri"],
    ["settings", "Setări"], ["media", "Fotografii"],
  ];

  const inputCls = "bg-cream border border-warm-grey/60 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/50";

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-main text-cream">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif text-xl">IMPERFECT <span className="italic text-sage">house</span></span>
            <span className="text-xs text-cream/50 hidden sm:inline">· panou administrare</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-xs text-cream/60 hover:text-terracotta">Vezi site-ul</a>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-1.5 text-xs text-cream/60 hover:text-terracotta">
              <LogOut size={14} /> Ieșire
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="admin-stats">
            {[["Sosiri azi", stats.arrivals], ["Plecări azi", stats.departures], ["Cereri noi", stats.pending],
              ["Ocupare 30z", `${stats.occupancy}%`], ["Venit luna", `${stats.revenue_month}€`], ["Quiz-uri", stats.quiz_count]].map(([l, v]) => (
              <div key={l} className="bg-cream-light border border-warm-grey/40 rounded-2xl p-4">
                <p className="text-[10px] uppercase tracking-widest font-bold text-walnut">{l}</p>
                <p className="mt-1 font-serif text-2xl text-main">{v}</p>
              </div>
            ))}
          </div>
        )}

        {stats && stats.quiz_count > 0 && (
          <div className="mt-4 bg-cream-light border border-warm-grey/40 rounded-2xl p-6" data-testid="admin-quiz-stats">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h3 className="font-serif text-xl text-main">Statistici quiz — ce caută oaspeții</h3>
              <button onClick={exportQuiz} data-testid="admin-export-quiz"
                className="flex items-center gap-2 rounded-full border border-warm-grey/60 text-brown text-xs font-semibold px-4 py-2 hover:border-terracotta">
                <Download size={13} /> Export CSV quiz
              </button>
            </div>
            <div className="mt-4 grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-walnut mb-3">Nopți recomandate</p>
                <div className="space-y-2">
                  {["1", "2", "3", "4", "5"].map((n) => {
                    const count = stats.quiz_nights?.[n] || 0;
                    const max = Math.max(1, ...Object.values(stats.quiz_nights || { 1: 1 }));
                    return (
                      <div key={n} className="flex items-center gap-3" data-testid={`quiz-nights-${n}`}>
                        <span className="text-sm font-semibold text-main w-14">{n} {n === "1" ? "noapte" : "nopți"}</span>
                        <div className="flex-1 h-2 bg-warm-grey/40 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-sage to-green-dark rounded-full" style={{ width: `${(count / max) * 100}%` }} />
                        </div>
                        <span className="text-sm text-brown/70 w-8 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-walnut mb-3">Cazare recomandată</p>
                <div className="flex gap-3">
                  {[["room", "Camere"], ["house", "Casa integrală"]].map(([k, label]) => (
                    <div key={k} className="flex-1 bg-cream rounded-xl px-4 py-4 text-center" data-testid={`quiz-unit-${k}`}>
                      <p className="font-serif text-3xl text-main">{stats.quiz_units?.[k] || 0}</p>
                      <p className="text-xs text-brown/60 mt-1">{label}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-brown/50">Rezultatele quiz-ului se salvează doar cu acordul vizitatorului, anonimizat.</p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {tabs.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === id ? "bg-main text-cream-light" : "bg-cream-light border border-warm-grey/50 text-brown hover:border-terracotta"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <div className="mt-6 space-y-4" data-testid="admin-bookings-panel">
            <div className="flex flex-wrap gap-3">
              <button onClick={() => setShowAdd(!showAdd)} data-testid="admin-add-booking-toggle"
                className="flex items-center gap-2 rounded-full bg-terracotta text-cream-light text-sm font-semibold px-5 py-2.5 hover:bg-terracotta/90">
                <Plus size={15} /> Rezervare manuală
              </button>
              <button onClick={exportCsv} data-testid="admin-export-csv"
                className="flex items-center gap-2 rounded-full border border-warm-grey/60 text-brown text-sm font-semibold px-5 py-2.5 hover:border-terracotta">
                <Download size={15} /> Export CSV
              </button>
            </div>

            {showAdd && (
              <form onSubmit={addBooking} className="bg-cream-light border border-warm-grey/40 rounded-2xl p-6 grid md:grid-cols-4 gap-3" data-testid="admin-add-booking-form">
                <select required value={newB.unit_id} onChange={(e) => setNewB({ ...newB, unit_id: e.target.value })} className={inputCls} data-testid="add-booking-unit">
                  <option value="">Unitate…</option>
                  {units.map((u) => <option key={u.id} value={u.id}>{u.name_ro}</option>)}
                </select>
                <input required placeholder="Nume oaspete" value={newB.guest_name} onChange={(e) => setNewB({ ...newB, guest_name: e.target.value })} className={inputCls} data-testid="add-booking-name" />
                <input placeholder="Telefon" value={newB.phone} onChange={(e) => setNewB({ ...newB, phone: e.target.value })} className={inputCls} data-testid="add-booking-phone" />
                <input type="number" min="0" placeholder="Avans €" value={newB.deposit} onChange={(e) => setNewB({ ...newB, deposit: e.target.value })} className={inputCls} data-testid="add-booking-deposit" />
                <label className="text-xs text-walnut font-bold">Check-in<input required type="date" value={newB.check_in} onChange={(e) => setNewB({ ...newB, check_in: e.target.value })} className={`${inputCls} mt-1 w-full`} data-testid="add-booking-checkin" /></label>
                <label className="text-xs text-walnut font-bold">Check-out<input required type="date" value={newB.check_out} onChange={(e) => setNewB({ ...newB, check_out: e.target.value })} className={`${inputCls} mt-1 w-full`} data-testid="add-booking-checkout" /></label>
                <input placeholder="Note interne" value={newB.notes} onChange={(e) => setNewB({ ...newB, notes: e.target.value })} className={`${inputCls} md:col-span-1`} data-testid="add-booking-notes" />
                <button className="rounded-full bg-main text-cream-light text-sm font-semibold px-5 py-2.5 hover:bg-terracotta" data-testid="add-booking-submit">Salvează</button>
              </form>
            )}

            <div className="overflow-x-auto bg-cream-light border border-warm-grey/40 rounded-2xl">
              <table className="w-full text-sm" data-testid="admin-bookings-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-walnut border-b border-warm-grey/40">
                    {["Oaspete", "Unitate", "Perioada", "Nopți", "Total", "Avans", "Status", "Sursa", "Acțiuni"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b border-warm-grey/20 last:border-0" data-testid={`booking-row-${b.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-main">{b.guest_name}</p>
                        <p className="text-xs text-brown/60">{b.phone}{b.pets ? " · 🐾" : ""}</p>
                      </td>
                      <td className="px-4 py-3 text-brown/80">{b.unit_name}</td>
                      <td className="px-4 py-3 text-brown/80 whitespace-nowrap">{fmtDate(b.check_in)} → {fmtDate(b.check_out)}</td>
                      <td className="px-4 py-3">{b.nights}</td>
                      <td className="px-4 py-3 font-semibold">{b.total}€</td>
                      <td className="px-4 py-3">{b.deposit}€</td>
                      <td className="px-4 py-3"><span className={`text-xs font-bold rounded-full px-3 py-1 ${STATUS_CLS[b.status]}`}>{STATUS[b.status]}</span></td>
                      <td className="px-4 py-3 text-xs text-brown/60">{b.source}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5">
                          {b.status === "pending" && (
                            <button onClick={() => patchBooking(b.id, { status: "confirmed" })} title="Confirmă" data-testid={`confirm-${b.id}`}
                              className="p-1.5 rounded-full bg-green-dark text-cream-light hover:opacity-80"><Check size={14} /></button>
                          )}
                          {b.status !== "cancelled" && (
                            <button onClick={() => patchBooking(b.id, { status: "cancelled" })} title="Anulează" data-testid={`cancel-${b.id}`}
                              className="p-1.5 rounded-full bg-terracotta/20 text-terracotta hover:bg-terracotta/30"><X size={14} /></button>
                          )}
                          <button onClick={async () => { await api.delete(`/admin/bookings/${b.id}`); load(); }} title="Șterge" data-testid={`delete-${b.id}`}
                            className="p-1.5 rounded-full bg-warm-grey/30 text-brown/60 hover:bg-warm-grey/50"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!bookings.length && <tr><td colSpan={9} className="px-4 py-10 text-center text-brown/50">Nicio rezervare încă.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "calendar" && (
          <div className="mt-6 grid lg:grid-cols-2 gap-6" data-testid="admin-calendar-panel">
            <form onSubmit={addBlock} className="bg-cream-light border border-warm-grey/40 rounded-2xl p-6 space-y-3" data-testid="admin-block-form">
              <h3 className="font-serif text-2xl text-main">Blochează o perioadă</h3>
              <select required value={newBlock.unit_id} onChange={(e) => setNewBlock({ ...newBlock, unit_id: e.target.value })} className={`${inputCls} w-full`} data-testid="block-unit">
                <option value="">Unitate…</option>
                {units.map((u) => <option key={u.id} value={u.id}>{u.name_ro}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-walnut font-bold">De la<input required type="date" value={newBlock.start_date} onChange={(e) => setNewBlock({ ...newBlock, start_date: e.target.value })} className={`${inputCls} mt-1 w-full`} data-testid="block-start" /></label>
                <label className="text-xs text-walnut font-bold">Până la<input required type="date" value={newBlock.end_date} onChange={(e) => setNewBlock({ ...newBlock, end_date: e.target.value })} className={`${inputCls} mt-1 w-full`} data-testid="block-end" /></label>
              </div>
              <input placeholder="Motiv (opțional)" value={newBlock.reason} onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })} className={`${inputCls} w-full`} data-testid="block-reason" />
              <button className="rounded-full bg-main text-cream-light text-sm font-semibold px-5 py-2.5 hover:bg-terracotta" data-testid="block-submit">Blochează</button>
            </form>

            <div className="bg-cream-light border border-warm-grey/40 rounded-2xl p-6" data-testid="admin-blocks-list">
              <h3 className="font-serif text-2xl text-main">Perioade blocate</h3>
              <ul className="mt-4 space-y-2">
                {blocks.map((b) => (
                  <li key={b.id} className="flex items-center justify-between bg-cream rounded-xl px-4 py-3 text-sm" data-testid={`block-${b.id}`}>
                    <span><strong>{units.find((u) => u.id === b.unit_id)?.name_ro || b.unit_id}</strong> · {fmtDate(b.start_date)} → {fmtDate(b.end_date)} {b.reason && <span className="text-brown/50">· {b.reason}</span>}</span>
                    <button onClick={async () => { await api.delete(`/admin/blocks/${b.id}`); load(); }} className="text-terracotta hover:opacity-70" data-testid={`block-delete-${b.id}`}><Trash2 size={15} /></button>
                  </li>
                ))}
                {!blocks.length && <li className="text-sm text-brown/50">Nicio perioadă blocată.</li>}
              </ul>
            </div>
          </div>
        )}

        {tab === "pricing" && (
          <div className="mt-6 space-y-6" data-testid="admin-pricing-panel">
            <div className="bg-cream-light border border-warm-grey/40 rounded-2xl p-6">
              <h3 className="font-serif text-2xl text-main">Prețuri de bază</h3>
              <div className="mt-4 grid md:grid-cols-3 gap-3">
                {units.map((u) => (
                  <div key={u.id} className="flex items-center justify-between bg-cream rounded-xl px-4 py-3" data-testid={`unit-price-${u.id}`}>
                    <span className="text-sm font-semibold text-main">{u.name_ro}</span>
                    <span className="flex items-center gap-2">
                      <input type="number" min="1" defaultValue={u.base_price} id={`price-${u.id}`} className="w-20 bg-cream-light border border-warm-grey/60 rounded-lg px-2 py-1.5 text-sm text-right" data-testid={`price-input-${u.id}`} />
                      <button onClick={() => saveUnitPrice(u.id, document.getElementById(`price-${u.id}`).value)} className="text-xs font-bold text-terracotta hover:underline" data-testid={`price-save-${u.id}`}>Salvează</button>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={addPrice} className="bg-cream-light border border-warm-grey/40 rounded-2xl p-6 grid md:grid-cols-4 gap-3" data-testid="admin-pricing-form">
              <h3 className="font-serif text-2xl text-main md:col-span-4">Regulă sezonieră nouă</h3>
              <select value={newPrice.unit_id} onChange={(e) => setNewPrice({ ...newPrice, unit_id: e.target.value })} className={inputCls} data-testid="pricing-unit">
                <option value="">Toate unitățile</option>
                {units.map((u) => <option key={u.id} value={u.id}>{u.name_ro}</option>)}
              </select>
              <input placeholder="Etichetă (ex: Crăciun)" value={newPrice.label} onChange={(e) => setNewPrice({ ...newPrice, label: e.target.value })} className={inputCls} data-testid="pricing-label" />
              <label className="text-xs text-walnut font-bold">De la<input required type="date" value={newPrice.start_date} onChange={(e) => setNewPrice({ ...newPrice, start_date: e.target.value })} className={`${inputCls} mt-1 w-full`} data-testid="pricing-start" /></label>
              <label className="text-xs text-walnut font-bold">Până la<input required type="date" value={newPrice.end_date} onChange={(e) => setNewPrice({ ...newPrice, end_date: e.target.value })} className={`${inputCls} mt-1 w-full`} data-testid="pricing-end" /></label>
              <input type="number" min="0" placeholder="Preț lucrătoare €" value={newPrice.price_weekday} onChange={(e) => setNewPrice({ ...newPrice, price_weekday: e.target.value })} className={inputCls} data-testid="pricing-weekday" />
              <input type="number" min="0" placeholder="Preț weekend €" value={newPrice.price_weekend} onChange={(e) => setNewPrice({ ...newPrice, price_weekend: e.target.value })} className={inputCls} data-testid="pricing-weekend" />
              <input type="number" min="1" placeholder="Min. nopți" value={newPrice.minimum_nights} onChange={(e) => setNewPrice({ ...newPrice, minimum_nights: e.target.value })} className={inputCls} data-testid="pricing-min-nights" />
              <button className="rounded-full bg-main text-cream-light text-sm font-semibold px-5 py-2.5 hover:bg-terracotta" data-testid="pricing-submit">Adaugă regula</button>
            </form>

            <div className="bg-cream-light border border-warm-grey/40 rounded-2xl p-6" data-testid="admin-pricing-list">
              <h3 className="font-serif text-2xl text-main">Reguli active</h3>
              <ul className="mt-4 space-y-2">
                {pricing.map((p) => (
                  <li key={p.id} className="flex items-center justify-between bg-cream rounded-xl px-4 py-3 text-sm" data-testid={`pricing-${p.id}`}>
                    <span>
                      <strong>{p.label || "Regulă"}</strong> · {units.find((u) => u.id === p.unit_id)?.name_ro || "Toate"} · {fmtDate(p.start_date)} → {fmtDate(p.end_date)}
                      {p.price_weekday && <span> · {p.price_weekday}€</span>}
                      {p.price_weekend && <span> / wknd {p.price_weekend}€</span>}
                      {p.minimum_nights > 1 && <span> · min {p.minimum_nights} nopți</span>}
                    </span>
                    <button onClick={async () => { await api.delete(`/admin/pricing/${p.id}`); load(); }} className="text-terracotta hover:opacity-70" data-testid={`pricing-delete-${p.id}`}><Trash2 size={15} /></button>
                  </li>
                ))}
                {!pricing.length && <li className="text-sm text-brown/50">Nicio regulă sezonieră — se aplică prețurile de bază.</li>}
              </ul>
            </div>
          </div>
        )}

        {tab === "settings" && (
          <form onSubmit={saveSettings} className="mt-6 bg-cream-light border border-warm-grey/40 rounded-2xl p-6 grid md:grid-cols-2 gap-4 max-w-3xl" data-testid="admin-settings-form">
            <h3 className="font-serif text-2xl text-main md:col-span-2">Setări site</h3>
            {[["whatsapp", "Număr WhatsApp (ex: 40721663181)"], ["phone_display", "Telefon afișat"], ["email", "Email contact"], ["notification_email", "Email pentru notificări rezervări"], ["score_general", "Scor general"], ["reviews_count", "Număr evaluări"], ["social_facebook", "Link Facebook"], ["social_instagram", "Link Instagram"], ["social_tiktok", "Link TikTok"]].map(([k, label]) => (
              <label key={k} className="text-xs text-walnut font-bold">{label}
                <input value={settingsForm[k] || ""} onChange={(e) => setSettingsForm({ ...settingsForm, [k]: e.target.value })} className={`${inputCls} mt-1 w-full`} data-testid={`setting-${k}`} />
              </label>
            ))}
            <div className="md:col-span-2 mt-2">
              <h3 className="font-serif text-2xl text-main">Setări email (SMTP)</h3>
              <p className="text-xs text-brown/60 mt-1">Folosite pentru notificările de rezervare și resetarea parolei. Lasă gol dacă nu ai încă un server de email — linkurile și notificările se vor înregistra în jurnalul serverului.</p>
            </div>
            {[["smtp_host", "Server SMTP (ex: smtp.mail.yahoo.com)"], ["smtp_port", "Port SMTP (ex: 587)"], ["smtp_user", "Utilizator SMTP (de obicei emailul)"], ["smtp_pass", "Parolă SMTP / parolă de aplicație"], ["smtp_from", "Adresă expeditor (From)"]].map(([k, label]) => (
              <label key={k} className="text-xs text-walnut font-bold">{label}
                <input type={k === "smtp_pass" ? "password" : "text"} value={settingsForm[k] || ""} onChange={(e) => setSettingsForm({ ...settingsForm, [k]: e.target.value })} className={`${inputCls} mt-1 w-full`} data-testid={`setting-${k}`} autoComplete="new-password" />
              </label>
            ))}
            <div className="md:col-span-2">
              <button className="rounded-full bg-main text-cream-light text-sm font-semibold px-6 py-3 hover:bg-terracotta" data-testid="settings-save">Salvează setările</button>
            </div>
          </form>
        )}

        {tab === "media" && (
          <div className="mt-6 space-y-6" data-testid="admin-media-panel">
            <div className="bg-cream-light border border-warm-grey/40 rounded-2xl p-6 flex flex-wrap items-end gap-3">
              <label className="text-xs text-walnut font-bold">Categorie
                <select value={uploadMeta.category} onChange={(e) => setUploadMeta({ ...uploadMeta, category: e.target.value })} className={`${inputCls} mt-1 block`} data-testid="media-category">
                  {["exterior", "camere", "bai", "living", "bar", "gradina", "foc", "cinema", "casa", "gazda", "animale", "imprejurimi", "general"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="text-xs text-walnut font-bold flex-1 min-w-[200px]">Text alternativ
                <input value={uploadMeta.alt_ro} onChange={(e) => setUploadMeta({ ...uploadMeta, alt_ro: e.target.value })} className={`${inputCls} mt-1 w-full`} data-testid="media-alt" />
              </label>
              <label className="flex items-center gap-2 rounded-full bg-terracotta text-cream-light text-sm font-semibold px-5 py-2.5 cursor-pointer hover:bg-terracotta/90" data-testid="media-upload-btn">
                <Upload size={15} /> Încarcă imagine
                <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="media-file-input" />
              </label>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {media.map((m) => (
                <div key={m.id} className="relative group rounded-2xl overflow-hidden border border-warm-grey/40" data-testid={`media-${m.id}`}>
                  <img src={`${process.env.REACT_APP_BACKEND_URL || ""}${m.url}`} alt={m.alt_ro} className="w-full aspect-square object-cover" />
                  <div className="absolute inset-x-0 bottom-0 bg-main/70 text-cream text-xs px-3 py-2 flex items-center justify-between">
                    <span>{m.category}</span>
                    <button onClick={async () => { await api.delete(`/admin/media/${m.id}`); load(); }} className="text-terracotta" data-testid={`media-delete-${m.id}`}><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
              {!media.length && <p className="text-sm text-brown/50 col-span-4">Nicio fotografie încărcată. Cele ~25 de imagini pot fi adăugate aici, pe categorii.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
